// Shipyard tab (04 §5.5): Habitat, the one production line, a build form per slice class, and the
// ships stationed here.
import type { FullSystem, Resources, ShipType } from '@stars-and-ships/api-types';
import { Check, Info, Lock, Rocket, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { formatDuration, formatNumber, formatShips, SHIP_LABELS, SHIP_ORDER } from '../../labels';
import { affordable } from '../../state/live';
import { batchCost, isPendingId } from '../../state/systemCache';
import { ShipInfo } from '../codex/ShipInfo';
import { Button, CostList, IconButton, NumberField, Panel, Select } from '../ui';
import { FinishNowButton } from './FinishNow';
import { parseAmount } from './shared';
import type { SystemActions, SystemTab } from './types';

/** One row of the requirements checklist (16a, P34): met or not, with an optional fix. */
function Requirement({
  met,
  label,
  detail,
  fix,
}: {
  met: boolean;
  label: string;
  detail: string;
  fix?: { label: string; onClick(): void } | undefined;
}) {
  return (
    <li data-requirement data-met={met ? 'true' : 'false'} className="flex items-start gap-2">
      {met ? (
        <Check className="mt-0.5 size-4 shrink-0 text-success" aria-label="met" />
      ) : (
        <X className="mt-0.5 size-4 shrink-0 text-danger" aria-label="not met" />
      )}
      <span className="min-w-0 flex-1">
        <span className={met ? 'text-text' : 'text-danger'}>{label}</span>{' '}
        <span className="text-xs text-faint">— {detail}</span>
      </span>
      {fix ? (
        <button
          type="button"
          className="shrink-0 text-xs text-accent underline-offset-2 hover:underline"
          onClick={fix.onClick}
        >
          {fix.label}
        </button>
      ) : null}
    </li>
  );
}

function ProductionPanel({
  galaxyId,
  system,
  have,
  now,
  actions,
  onTabChange,
}: {
  galaxyId: string;
  system: FullSystem;
  have: Resources;
  now: number;
  actions: SystemActions | undefined;
  onTabChange?: ((tab: SystemTab) => void) | undefined;
}) {
  const classes = SHIP_ORDER.filter((type) => system.shipOptions[type]);
  const firstBuildable = classes.find((type) => !system.shipOptions[type]?.blocker) ?? classes[0];
  const [chosen, setChosen] = useState<ShipType | undefined>(undefined);
  const shipType = chosen ?? firstBuildable;
  const [infoOpen, setInfoOpen] = useState(false);
  // Empty at the start and after each order (16a): the preview then shows one ship.
  const [quantity, setQuantity] = useState('');
  const option = shipType ? system.shipOptions[shipType] : undefined;
  const count = parseAmount(quantity);
  const shown = Math.max(1, count);
  const cost = batchCost(option?.cost ?? { metal: 0, gas: 0, supplies: 0 }, shown);
  const { used, capacity } = system.habitat;
  const free = Math.max(0, capacity - used);
  const points = (option?.habitatPoints ?? 0) * shown;
  const dockLevel = system.facilities.space_dock ?? 0;
  const dockMet = !option?.blocker;
  const habitatMet = points <= free;
  const resourcesMet = affordable(have, cost);
  const problem = !option
    ? 'No ship classes available.'
    : !dockMet || !habitatMet || !resourcesMet
      ? 'requirements'
      : count < 1
        ? 'Enter how many ships to build.'
        : null;
  const pct = capacity > 0 ? Math.min(100, (used / capacity) * 100) : 100;

  return (
    <Panel title="Shipyard">
      <div data-habitat className="text-sm">
        <div className="flex justify-between gap-2">
          <span className="text-muted">Habitat</span>
          <span className="font-mono tabular-nums text-text">
            {formatNumber(used)} / {formatNumber(capacity)}
          </span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-panel-raised">
          <div
            className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-danger' : 'bg-influence'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {system.shipProduction.length === 0 ? (
          <li className="hud-cut-sm border border-dashed border-line p-3 text-sm text-faint">
            The production line is free
          </li>
        ) : (
          system.shipProduction.map((item) => {
            const next = item.nextShipAt ? Date.parse(item.nextShipAt) : null;
            return (
              <li
                key={item.id}
                data-production-item={item.id}
                className="hud-cut-sm border border-line bg-void/60 p-3"
              >
                {/* Name and status on top, progress, then the buttons on their own line (16e feedback). */}
                <div className="flex items-baseline justify-between gap-3">
                  <p className="min-w-0 break-words text-sm text-text">
                    {SHIP_LABELS[item.shipType]} · {formatNumber(item.delivered)}/
                    {formatNumber(item.quantity)}
                  </p>
                  <p className="shrink-0 font-mono text-xs tabular-nums text-info">
                    {next === null
                      ? 'waiting in line'
                      : next > now
                        ? `next ship in ${formatDuration(next - now)}`
                        : 'delivering…'}
                  </p>
                </div>
                <div className="mt-2 h-1 rounded-full bg-panel-raised">
                  <div
                    className="h-1 rounded-full bg-accent"
                    style={{ width: `${(item.delivered / item.quantity) * 100}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-end gap-1">
                  {next !== null &&
                  item.completesAt &&
                  actions?.finishNow &&
                  !isPendingId(item.id) ? (
                    <FinishNowButton
                      context={actions.finishNow}
                      kind="ships"
                      itemId={item.id}
                      endsAt={Date.parse(item.completesAt)}
                      now={now}
                      title={`Finish ${formatShips({ [item.shipType]: item.quantity - item.delivered })} now?`}
                    />
                  ) : null}
                  <Button
                    variant="ghost"
                    disabled={!actions || isPendingId(item.id)}
                    pending={actions?.pending.cancelProduction === item.id}
                    onClick={() => actions?.cancelProduction(item.id)}
                  >
                    Cancel
                  </Button>
                </div>
              </li>
            );
          })
        )}
      </ul>
      {classes.length > 0 ? (
        <form
          className="mt-3 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!problem && shipType) {
              actions?.produce(shipType, count);
              setQuantity('');
            }
          }}
        >
          <div className="grid grid-cols-[1fr_auto_6rem] items-end gap-2">
            <Select
              label="Ship class"
              value={shipType}
              onChange={(e) => setChosen(e.target.value as ShipType)}
            >
              {classes.map((type) => {
                const blocker = system.shipOptions[type]?.blocker;
                return (
                  <option key={type} value={type}>
                    {blocker
                      ? `🔒 ${SHIP_LABELS[type]} — needs Space Dock Lv ${blocker.requiredLevel}`
                      : SHIP_LABELS[type]}
                  </option>
                );
              })}
            </Select>
            {shipType ? (
              <IconButton
                label={`About ${SHIP_LABELS[shipType]}`}
                className="border border-line"
                onClick={() => setInfoOpen(true)}
              >
                <Info className="size-4" aria-hidden />
              </IconButton>
            ) : (
              <span />
            )}
            <NumberField
              label="Quantity"
              min={1}
              placeholder="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          {option ? (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <CostList cost={cost} available={have} />
              <span className="font-mono text-xs text-faint">
                {count < 1 ? 'per ship · ' : ''}
                {formatDuration(option.unitDurationMs * shown)} · {formatNumber(points)} Habitat
              </span>
            </div>
          ) : null}
          {option ? (
            <div data-requirements>
              <p className="flex items-center gap-1 text-xs uppercase tracking-[0.12em] text-muted">
                {dockMet ? null : <Lock className="size-3.5" aria-hidden />}
                Requirements
              </p>
              <ul className="mt-1 space-y-1 text-sm">
                <Requirement
                  met={dockMet}
                  label={
                    option.blocker
                      ? `Space Dock Lv ${option.blocker.requiredLevel}`
                      : 'Space Dock level'
                  }
                  detail={`this system has Lv ${dockLevel}`}
                  fix={
                    !dockMet && onTabChange
                      ? { label: 'Upgrade in Buildings', onClick: () => onTabChange('buildings') }
                      : undefined
                  }
                />
                <Requirement
                  met={habitatMet}
                  label={`Habitat ${formatNumber(points)} points`}
                  detail={`${formatNumber(free)} free`}
                />
                <Requirement
                  met={resourcesMet}
                  label="Resources in storage"
                  detail={resourcesMet ? 'enough stored' : 'red costs are missing'}
                />
              </ul>
            </div>
          ) : null}
          {problem && problem !== 'requirements' && count > 0 ? (
            <p className="text-xs text-danger">{problem}</p>
          ) : null}
          <Button
            type="submit"
            className="w-full"
            disabled={!actions || problem !== null}
            pending={actions?.pending.produce ?? false}
          >
            Build ships
          </Button>
        </form>
      ) : null}
      {shipType && infoOpen ? (
        <ShipInfo galaxyId={galaxyId} ship={shipType} open onClose={() => setInfoOpen(false)} />
      ) : null}
    </Panel>
  );
}

/** Send-fleet link in the secondary-button look (a link, so it opens in a new tab too). */
function SendFleetLink({ galaxyId, systemId }: { galaxyId: string; systemId: string }) {
  return (
    <Link
      to={`/g/${galaxyId}/fleet/send?origin=${systemId}`}
      className="inline-flex min-h-11 shrink-0 items-center gap-2 hud-cut-sm border border-line-strong bg-panel-raised px-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-text hover:border-accent"
    >
      <Rocket className="size-4" aria-hidden /> Send fleet from here
    </Link>
  );
}

function StationedPanel({ galaxyId, system }: { galaxyId: string; system: FullSystem }) {
  const garrison = system.fleets.find((f) => f.isGarrison);
  const garrisonShips = Object.entries(garrison?.ships ?? {}).filter(([, n]) => (n ?? 0) > 0);
  const reinforcements = system.fleets.filter((f) => !f.isGarrison);
  return (
    <Panel title="Stationed here">
      {garrisonShips.length === 0 && reinforcements.length === 0 ? (
        <p className="text-sm text-muted">No ships stationed.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {garrisonShips.map(([ship, count]) => (
            <li key={ship} className="flex justify-between">
              <span className="text-muted">{SHIP_LABELS[ship as ShipType]}</span>
              <span className="font-mono tabular-nums text-text">{formatNumber(count ?? 0)}</span>
            </li>
          ))}
          {reinforcements.map((f) => (
            <li key={f.id} className="flex justify-between gap-2">
              <span className="text-muted">Reinforcement</span>
              <span className="text-right font-mono text-xs tabular-nums text-text">
                {formatShips(f.ships)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <SendFleetLink galaxyId={galaxyId} systemId={system.id} />
      </div>
    </Panel>
  );
}

export function ShipyardTab({
  galaxyId,
  system,
  have,
  now,
  actions,
  onTabChange,
}: {
  galaxyId: string;
  system: FullSystem;
  have: Resources;
  now: number;
  actions: SystemActions | undefined;
  onTabChange?: ((tab: SystemTab) => void) | undefined;
}) {
  return (
    <div className="grid gap-4 2xl:grid-cols-2 2xl:items-start">
      <ProductionPanel
        galaxyId={galaxyId}
        system={system}
        have={have}
        now={now}
        actions={actions}
        onTabChange={onTabChange}
      />
      <StationedPanel galaxyId={galaxyId} system={system} />
    </div>
  );
}
